import { createContext, useCallback, useContext } from "react";
import { ActionPlanData } from "src/context/Actions/useActionPlanData";
import { useGetAllActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { useGetOneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ScoreService } from "src/services/ScoreService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useActionPlanData } from "src/context/Actions/useActionPlanData";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { getDefaultVariationIdsByType, getVariationIdsByType, getVariationOrderByType } from "src/utils/scores";

export type PuckActionPlanContextValue = {
    isLoading: boolean;
    isError: boolean;
    actionPlanData: ActionPlanData[];
    actionPlanStats: {
        total: number;
        engagedActions: number;
    };
    setVariation: (scoreId: number, scoreVariationId: number | null) => void;
};

const PuckActionPlanContext = createContext<PuckActionPlanContextValue | null>(null);

export type HeadingScore = {
    id: string;
    label: string;
    currentScore: number | null;
    simulatedScore: number | null;
    difference: number | null;
    differencePercentage: number | null;
    unit: string;
    children?: HeadingScore[];
};

type ProviderProps = {
    children: ReactNode;
};

export const PuckActionPlanProvider = (props: ProviderProps) => {
    const { children } = props;
    const { selectedCompany, getScoresByType, scoreTypes } = useFoodpilotContext();

    const ssqId =
        selectedCompany?.organizationSector?.primarySsqId ?? selectedCompany?.organizationSector?.ssqId ?? null;

    const companyScores = getScoresByType("company") || [];
    const companyVariationsIds = getVariationIdsByType("company", scoreTypes, companyScores);
    const defaultVariationsIds = getDefaultVariationIdsByType("company", scoreTypes);
    const companySectorVariationsIds = selectedCompany?.organizationSector?.variationIds;
    const companySectorDefaultVariationsIds =
        (companySectorVariationsIds ? selectedCompany.organizationSector?.defaultVariationIds : defaultVariationsIds) ??
        [];

    const scoresParams = useOlapScoresParams({
        scores: {
            dimensions: [[["headings"]]],
        },
    });

    const lastSelectedVariation = scoresParams.state.lastSelectedVariation;
    const lastSelectedScoreId = lastSelectedVariation?.scoreId ?? null;
    const lastSelectedVariationId = lastSelectedVariation?.variationId ?? null;
    const selectVariation = scoresParams.state.selectVariation;

    const {
        data: actionPlans,
        isFetched: areAllActionsPlansFetched,
        isLoading: isAllActionPlansLoading,
    } = useGetAllActionPlan({
        queryParam:
            selectedCompany?.id && ssqId !== null ?
                {
                    "startSsqPost.organization.id": selectedCompany.id,
                    "startSsqPost.ssqId": ssqId,
                }
            :   undefined,
        enabled: Boolean(selectedCompany?.id) && ssqId !== null,
    });

    const selectedActionPlanID = actionPlans?.[0]?.id;

    const {
        data: actionPlan,
        isFetched: isSingleActionPlanFetched,
        isLoading: isSingleActionPlanLoading,
    } = useGetOneActionPlan({
        actionPlanId: selectedActionPlanID,
        scoresQuery: scoresParams.query,
    });

    const scoresWithVariations = ScoreService.getScoresWithVariations(
        companyScores,
        companySectorVariationsIds ?? companyVariationsIds,
        companySectorDefaultVariationsIds,
        getVariationOrderByType("company", scoreTypes),
    );

    const isFetched = areAllActionsPlansFetched && ((actionPlans?.length ?? 0) > 0 ? isSingleActionPlanFetched : true);
    const isLoading = (isAllActionPlansLoading || isSingleActionPlanLoading) && !isFetched;

    const actionPlanWithDefaults =
        actionPlan ?
            {
                ...actionPlan,
                startSsqPost: actionPlan.startSsqPost || { id: 0, olapScores: {}, elements: [] },
                actionPlanSsqPosts: actionPlan.actionPlanSsqPosts || [],
                simulationSsqPosts: actionPlan.simulationSsqPosts || [],
            }
        :   undefined;

    const actionPlanData = useActionPlanData({
        actionPlan: actionPlanWithDefaults,
        scores: scoresWithVariations,
    });

    const setVariation = useCallback(
        (scoreId: number, scoreVariationId: number | null) => {
            if (lastSelectedScoreId === scoreId && lastSelectedVariationId === scoreVariationId) {
                return;
            }

            selectVariation(scoreId, scoreVariationId);
        },
        [lastSelectedScoreId, lastSelectedVariationId, selectVariation],
    );

    const actionPlanStats = {
        total: 0,
        engagedActions: (actionPlan?.actionPlanSsqPosts ?? []).length,
    };

    const contextValue = {
        isLoading,
        isError: isFetched && (!actionPlanData || actionPlanData.length === 0),
        actionPlanData: actionPlanData || [],
        setVariation,
        actionPlanStats,
    };

    return <PuckActionPlanContext.Provider value={contextValue}>{children}</PuckActionPlanContext.Provider>;
};

export const usePuckActionPlan = () => {
    const context = useContext(PuckActionPlanContext);

    if (!context) {
        return {
            isLoading: false,
            isError: false,
            actionPlanData: [],
            setVariation: () => {},
            actionPlanStats: {
                total: 0,
                engagedActions: 0,
            },
        };
    }

    return context;
};
